import { SubscriptionSeatTypes } from "@safetyculture/s12-apis-connect-web/s12/common/seat_types_pb";
import { SortDirection } from "@safetyculture/s12-apis-connect-web/s12/common/sorting_pb";
import {
  GetFoldersByUsersRequest,
  GetFoldersByUsersResponse,
} from "@safetyculture/s12-apis-connect-web/s12/directory/v1/directory_pb";
import {
  AdminGetGroupsByOrgRequest,
  AdminGetGroupsByOrgResponse,
} from "@safetyculture/s12-apis-connect-web/s12/groups/admin/v1/admin_groups_pb";
import {
  Filter,
  GroupType,
  PaginationOption,
} from "@safetyculture/s12-apis-connect-web/s12/groups/v1/common_pb";
import {
  GetOrganizationRequest,
  GetOrganizationResponse,
  GetOrganizationsRequest,
  GetOrganizationsResponse,
} from "@safetyculture/s12-apis-connect-web/s12/identity/admin/v1/identity_management_pb";
import {
  GetSubscriptionRequest,
  GetSubscriptionResponse,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import {
  BulkUpdateUsersSeatTypeRequest,
  BulkUpdateUsersSeatTypeResponse,
  GetOrgDetailsRequest,
  GetOrgDetailsResponse,
  GetOrgInspectionQuotaRequest,
  GetOrgInspectionQuotaResponse,
  GetOrgWithSubscriptionRequest,
  GetOrgWithSubscriptionResponse,
  GetOrganisationUserListRequest,
  GetOrganisationUserListResponse,
  ListOrganisationGroupsRequest,
  ListOrganisationGroupsResponse,
  ListUsersLastSeenRequest,
  ListUsersLastSeenResponse,
  Pagination,
  PromoteUserToOrgAdminRequest,
  PromoteUserToOrgAdminResponse,
  RemoveUserFromOrganisationRequest,
  RemoveUserFromOrganisationResponse,
  SetExcludeOrgFromAnalyticsRequest,
  SetExcludeOrgFromAnalyticsResponse,
  SuspendOrgRequest,
  SuspendOrgResponse,
  SuspendOrganizationRequest,
  SuspendOrganizationResponse,
  TransferMultiOrgUserRequest,
  TransferMultiOrgUserResponse,
  UnsuspendOrganizationRequest,
  UnsuspendOrganizationResponse,
  UpdateOrgDefaultTemplateAccessRequest,
  UpdateOrgDefaultTemplateAccessResponse,
  UpdateOrgInspectionQuotaRequest,
  UpdateOrgInspectionQuotaResponse,
  UpdateUsersActiveStatusRequest,
  UpdateUsersActiveStatusResponse,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import {
  UserSortType,
  UserStatus,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_user_pb";
import {
  BillingProvider,
  CreateOrganisationRequest,
  CreateOrganisationResponse,
  Environment,
  MigrationRegion,
  Region,
  SubscriptionState,
  SubscriptionTerm,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import { supportServiceConnectClient } from "../index";
import { RequestMetadata } from "api/types";

type GetOrgWithSubscriptionParams = {
  roleId: string;
};

export const getOrgWithSubscription = async (
  { roleId }: GetOrgWithSubscriptionParams,
  headers: Record<RequestMetadata, string>
): Promise<GetOrgWithSubscriptionResponse> => {
  const req = new GetOrgWithSubscriptionRequest({
    roleId,
  });

  return supportServiceConnectClient.getOrgWithSubscription(req, { headers });
};

type GetOrganizationIdentityParams = {
  orgId: string;
};

export const getOrganizationIdentity = async (
  { orgId }: GetOrganizationIdentityParams,
  headers: Record<RequestMetadata, string>
): Promise<GetOrganizationResponse> => {
  const req = new GetOrganizationRequest({
    orgId,
  });

  return supportServiceConnectClient.getOrganizationIdentity(req, { headers });
};

type RemoveUserFromOrgParams = {
  userId: string;
  roleId: string;
  assigneeId?: string;
};

export const removeUserFromOrg = async (
  { userId, roleId, assigneeId }: RemoveUserFromOrgParams,
  headers: Record<RequestMetadata, string>
): Promise<RemoveUserFromOrganisationResponse> => {
  const req = new RemoveUserFromOrganisationRequest({
    userId,
    roleId,
  });

  if (assigneeId !== undefined) {
    req.assigneeUserId = assigneeId;
  }

  return supportServiceConnectClient.removeUserFromOrganisation(req, {
    headers,
  });
};

type PromoteUserToAdminParams = {
  orgId: string;
  userId: string;
};

export const promoteUserToAdmin = async (
  { orgId, userId }: PromoteUserToAdminParams,
  headers: Record<RequestMetadata, string>
): Promise<PromoteUserToOrgAdminResponse> => {
  const req = new PromoteUserToOrgAdminRequest({
    orgId,
    userId,
  });

  return supportServiceConnectClient.promoteUserToOrgAdmin(req, { headers });
};

type UpdateUsersActiveStatusParams = {
  userIds: string[];
  orgId: string;
  active: boolean;
  seatType?: number;
};

export const updateUsersActiveStatus = async (
  { userIds, orgId, active, seatType }: UpdateUsersActiveStatusParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateUsersActiveStatusResponse> => {
  const req = new UpdateUsersActiveStatusRequest({
    userIds,
    orgId,
    activeStatus: active,
  });

  if (seatType !== undefined) {
    req.seatType = seatType;
  }

  return supportServiceConnectClient.updateUsersActiveStatus(req, { headers });
};

type UpdateUsersSeatTypeParams = {
  userIds: string[];
  orgId: string;
  seatType: number;
};

export const updateUsersSeatType = async (
  { userIds, orgId, seatType }: UpdateUsersSeatTypeParams,
  headers: Record<RequestMetadata, string>
): Promise<BulkUpdateUsersSeatTypeResponse> => {
  const req = new BulkUpdateUsersSeatTypeRequest({
    userIds,
    orgId,
    seatType,
  });

  return supportServiceConnectClient.bulkUpdateUsersSeatType(req, { headers });
};

type TransferMultiOrgUserParams = {
  sourceUserId: string;
  sourceOrgId: string;
  sourceRegion: MigrationRegion;
  sourceUserEmail: string;
  targetOrgId: string;
  targetSeatType: SubscriptionSeatTypes;
};

export const transferMultiOrgUser = async (
  {
    sourceUserId,
    sourceOrgId,
    sourceRegion,
    sourceUserEmail,
    targetOrgId,
    targetSeatType,
  }: TransferMultiOrgUserParams,
  headers: Record<RequestMetadata, string>
): Promise<TransferMultiOrgUserResponse> => {
  const req = new TransferMultiOrgUserRequest({
    sourceUserId,
    sourceOrgId,
    sourceRegion,
    sourceUserEmail,
    targetOrgId,
    targetSeatType,
  });

  return supportServiceConnectClient.transferMultiOrgUser(req, {
    headers,
  });
};

type ListOrganisationGroupsParams = {
  orgId: string;
};

export const listOrganisationGroups = async (
  { orgId }: ListOrganisationGroupsParams,
  headers: Record<RequestMetadata, string>
): Promise<ListOrganisationGroupsResponse> => {
  const req = new ListOrganisationGroupsRequest({
    orgId,
  });

  return supportServiceConnectClient.listOrganisationGroups(req, { headers });
};

type GetOrganizationGroupsParams = {
  organizationId: string;
  pageToken?: string;
  includeDeleted?: boolean;
  pagingOption?: PaginationOption;
  groupType?: GroupType;
  includeMembers?: boolean;
  filter?: Filter;
};

export const getOrganizationGroups = async (
  {
    organizationId,
    pageToken,
    includeDeleted,
    pagingOption,
    groupType,
    includeMembers,
    filter,
  }: GetOrganizationGroupsParams,
  headers: Record<RequestMetadata, string>
): Promise<AdminGetGroupsByOrgResponse> => {
  const req = new AdminGetGroupsByOrgRequest({
    organisationId: organizationId,
    pageToken,
    includeDeleted,
    pagingOption,
    groupType,
    includeMembers: includeMembers || false,
    filter,
  });

  return supportServiceConnectClient.getOrganizationGroups(req, { headers });
};

export type GetUsersListParams = {
  organizationId: string;
  pageSize: number;
  offset: number;
  includeRoles?: boolean;
  includeInactiveRoles?: boolean;
  allowUsersOutsideOrg?: boolean;
  sortDirection: SortDirection;
  sortType: UserSortType;
  userIds?: string[];
  query?: string;
  adminOnly?: boolean;
  userStatus: UserStatus;
  seatType?: SubscriptionSeatTypes;
};

export const getOrgUserList = async (
  {
    organizationId,
    pageSize,
    offset,
    includeRoles = false,
    includeInactiveRoles,
    allowUsersOutsideOrg = false,
    sortDirection,
    sortType,
    userIds,
    query,
    adminOnly,
    userStatus,
    seatType = SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED,
  }: GetUsersListParams,
  headers: Record<RequestMetadata, string>
): Promise<GetOrganisationUserListResponse> => {
  const pagination = new Pagination({
    pageSize,
    offset,
  });

  const req = new GetOrganisationUserListRequest({
    roleId: organizationId,
    pagination,
    includeRoles,
    includeInactiveRoles: includeInactiveRoles || false,
    allowUserIdsOutsideOrg: allowUsersOutsideOrg,
    adminOnly: adminOnly || false,
    userStatus,
    sortDirection: sortDirection || SortDirection.ASC,
    sortType: sortType || UserSortType.FIRST_NAME,
    seatType,
  });

  if (query) {
    req.query = query;
  }

  if (userIds && userIds.length > 0) {
    req.userIds = userIds;
  }

  return supportServiceConnectClient.getOrganisationUserList(req, { headers });
};

type ListUsersLastSeenParams = {
  orgId: string;
  userIds?: string[];
};

export const listUsersLastSeen = async (
  { orgId, userIds }: ListUsersLastSeenParams,
  headers: Record<RequestMetadata, string>
): Promise<ListUsersLastSeenResponse> => {
  const req = new ListUsersLastSeenRequest({
    orgId,
  });

  if (userIds && userIds.length > 0) {
    req.userIds = userIds;
  }

  return supportServiceConnectClient.listUsersLastSeen(req, { headers });
};

type GetFoldersByUsersParams = {
  orgId: string;
  userIds?: string[];
};

export const GetFoldersByUsers = async (
  { orgId, userIds }: GetFoldersByUsersParams,
  headers: Record<RequestMetadata, string>
): Promise<GetFoldersByUsersResponse> => {
  const req = new GetFoldersByUsersRequest({
    orgId,
    userIds,
  });

  return supportServiceConnectClient.getFoldersByUsers(req, { headers });
};

// @Deprecated. Use SuspendOrganization and UnsuspendOrganization instead.
type SuspendOrgParams = {
  orgId: string;
  active: boolean;
  region: Region;
};
export const setOrgIsActive = async (
  { orgId, active, region }: SuspendOrgParams,
  headers: Record<RequestMetadata, string>
): Promise<SuspendOrgResponse> => {
  const req = new SuspendOrgRequest({
    orgId,
    active,
    region,
  });
  return supportServiceConnectClient.suspendOrg(req, { headers });
};

type SuspendOrganizationParams = {
  organizationId: string;
  region: Region;
};
export const suspendOrganization = async (
  { organizationId, region }: SuspendOrganizationParams,
  headers: Record<RequestMetadata, string>
): Promise<SuspendOrganizationResponse> => {
  const req = new SuspendOrganizationRequest({
    organizationId,
    region,
  });
  return supportServiceConnectClient.suspendOrganization(req, { headers });
};

type UnsuspendOrganizationParams = {
  organizationId: string;
};
export const unsuspendOrganization = async (
  { organizationId }: UnsuspendOrganizationParams,
  headers: Record<RequestMetadata, string>
): Promise<UnsuspendOrganizationResponse> => {
  const req = new UnsuspendOrganizationRequest({
    organizationId,
  });
  return supportServiceConnectClient.unsuspendOrganization(req, { headers });
};

export type GetOrganizationsByOrgIDsParams = {
  orgIds: string[];
};

export const getOrganizationsByOrgIDs = async (
  { orgIds }: GetOrganizationsByOrgIDsParams,
  headers: Record<RequestMetadata, string>
): Promise<GetOrganizationsResponse> => {
  const req = new GetOrganizationsRequest({
    orgIds,
  });

  return supportServiceConnectClient.getOrganizationsByOrgIDs(req, { headers });
};

export type GetOrgInspectionQuotaParams = {
  orgId: string;
};

export const getOrgInspectionQuota = async (
  { orgId }: GetOrgInspectionQuotaParams,
  headers: Record<RequestMetadata, string>
): Promise<GetOrgInspectionQuotaResponse> => {
  const req = new GetOrgInspectionQuotaRequest({
    orgId,
  });

  return supportServiceConnectClient.getOrgInspectionQuota(req, { headers });
};

export type UpdateOrgInspectionQuotaParams = {
  orgId: string;
  seatType: SubscriptionSeatTypes;
  quota: number;
};

export const updateOrgInspectionQuota = async (
  { orgId, seatType, quota }: UpdateOrgInspectionQuotaParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateOrgInspectionQuotaResponse> => {
  const req = new UpdateOrgInspectionQuotaRequest({
    orgId,
    seatType,
    quota,
  });

  return supportServiceConnectClient.updateOrgInspectionQuota(req, { headers });
};

export type GetOrgDetailsParams = {
  roleId: string;
  environment: Environment;
};

export const getOrgDetails = async (
  { roleId, environment }: GetOrgDetailsParams,
  headers: Record<RequestMetadata, string>
): Promise<GetOrgDetailsResponse> => {
  const req = new GetOrgDetailsRequest({
    roleId,
    environment,
  });

  return supportServiceConnectClient.getOrgDetails(req, { headers });
};

export const getSubscription = async (
  { organizationId }: { organizationId: string },
  headers: Record<RequestMetadata, string>
): Promise<GetSubscriptionResponse> => {
  const req = new GetSubscriptionRequest({
    organizationId,
  });

  return supportServiceConnectClient.getSubscription(req, { headers });
};

export type SetExcludeOrgFromAnalyticsParams = {
  orgId: string;
  excludeFromAnalytics: boolean;
};

export const setExcludeOrgFromAnalytics = async (
  { orgId, excludeFromAnalytics }: SetExcludeOrgFromAnalyticsParams,
  headers: Record<RequestMetadata, string>
): Promise<SetExcludeOrgFromAnalyticsResponse> => {
  const req = new SetExcludeOrgFromAnalyticsRequest({
    orgId,
    excludeFromAnalytics,
  });

  return supportServiceConnectClient.setExcludeOrgFromAnalytics(req, {
    headers,
  });
};

export type UpdateOrgDefaultTemplateAccessParams = {
  orgId: string;
  preset: string;
};

export const updateOrgDefaultTemplateAccess = async (
  { orgId, preset }: UpdateOrgDefaultTemplateAccessParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateOrgDefaultTemplateAccessResponse> => {
  const req = new UpdateOrgDefaultTemplateAccessRequest({
    orgId,
    preset,
  });

  return supportServiceConnectClient.updateOrgDefaultTemplateAccess(req, {
    headers,
  });
};

export type CreateOrganisationParams = {
  orgName: string;
  password: string;
  firstName: string;
  lastName: string;
  billingProvider: BillingProvider;
  subscriptionState: SubscriptionState;
  subscriptionTerm: SubscriptionTerm;
  couponCode: string;
};

export { BillingProvider, SubscriptionState, SubscriptionTerm };
export type { CreateOrganisationResponse };

export const createOrganisation = async (
  {
    orgName,
    password,
    firstName,
    lastName,
    billingProvider,
    subscriptionState,
    subscriptionTerm,
    couponCode,
  }: CreateOrganisationParams,
  headers: Record<RequestMetadata, string>
): Promise<CreateOrganisationResponse> => {
  const req = new CreateOrganisationRequest({
    orgName,
    password,
    firstName,
    lastName,
    billingProvider,
    subscriptionState,
    subscriptionTerm,
    couponCode,
  });

  return supportServiceConnectClient.createOrganisation(req, { headers });
};
